import { tempoApi } from '@/lib/api/tempoApi';
import type {
  JiraCustomFieldFilter,
  JiraFields,
  JiraInstance,
  JiraIssueType,
  JiraIssueTypesAndStatusesResponse,
  JiraIssueTypesWithHierarchyResponse,
  JiraItemLevelMapping,
  JiraItemTypeMapping,
  JiraOauthRequestResponse,
  JiraOauthResponseResponse,
  JiraProject,
  JiraProjectItemCountResponse,
  ValidateJqlRequest,
  ValidateJqlResponse,
} from '@/lib/api/types/work-integrations';

const getInstances = async () => {
  const response = await tempoApi().get('api/v1/work-integration/instances').json<JiraInstance[]>();
  return response;
};

const getJiraInstanceIssueTypes = async ({ instanceId }: { instanceId: string }) => {
  const response = await tempoApi()
    .get(`api/v1/work-integration/instances/${instanceId}/issue-types`)
    .json<JiraIssueType[]>();
  return response;
};

const getJiraIssueTypesAndStatuses = async ({
  projectIdOrKey,
  cloudId,
}: {
  cloudId: string;
  projectIdOrKey: string;
}) => {
  const response = await tempoApi()
    .get(`api/v1/work-integration/instances/${cloudId}/projects/${projectIdOrKey}`)
    .json<JiraIssueTypesAndStatusesResponse>();
  return response;
};

const getJiraIssueTypesWithHierarchy = async ({
  projectIdOrKey,
  cloudId,
}: {
  cloudId: string;
  projectIdOrKey: string;
}) => {
  const response = await tempoApi()
    .get(`api/v1/work-integration/instances/${cloudId}/projects/${projectIdOrKey}/issue-types`)
    .json<JiraIssueTypesWithHierarchyResponse[]>();
  return response;
};

const getJiraProjects = async ({ cloudId }: { cloudId: string }) => {
  const response = await tempoApi()
    .get(`api/v1/work-integration/instances/${cloudId}/projects`)
    .json<JiraProject[]>();
  return response;
};

const getJiraFields = async ({
  instanceId,
  projectId,
}: {
  instanceId: string;
  projectId?: string;
}) => {
  const base = `api/v1/work-integration/instances/${instanceId}/fields`;
  const params = projectId ? `?projectId=${projectId}` : '';

  const response = await tempoApi().get(`${base}${params}`).json<JiraFields>();
  return response;
};

const getJiraWorkItemCount = async ({
  projectIdOrKey,
  cloudId,
  typeIds,
  statusIds,
  includeWorkAfter,
  jql,
  customFieldFilters,
}: {
  cloudId: string;
  projectIdOrKey: string;
  typeIds?: string[];
  statusIds?: string[];
  includeWorkAfter?: string;
  jql?: string;
  customFieldFilters?: JiraCustomFieldFilter[];
}) => {
  const response = await tempoApi()
    .post(
      `api/v1/work-integration/instances/${cloudId}/projects/${projectIdOrKey}/approximate-count`,
      {
        json: {
          filters: {
            typeIds,
            statusIds,
            includeWorkAfter,
            jql,
            customFieldFilters,
          },
        },
      },
    )
    .json<JiraProjectItemCountResponse>();
  return response.count;
};

const validateJiraJql = async ({ cloudId, jql }: { cloudId: string; jql: string }) => {
  const response = await tempoApi()
    .post(`api/v1/work-integration/instances/${cloudId}/jql/validate`, {
      json: { jql } as ValidateJqlRequest,
    })
    .json<ValidateJqlResponse>();
  return response;
};

const jiraOAuthRequest = async ({
  returnUrl,
  instanceId,
}: {
  returnUrl: string;
  instanceId?: string;
}): Promise<JiraOauthRequestResponse> => {
  return await tempoApi()
    .post('api/v1/work-integration/jira/oauth-client/authorization-request', {
      json: { returnUrl, instanceId },
    })
    .json<JiraOauthRequestResponse>();
};

const jiraOAuthResponse = async ({ returnUrl }: { returnUrl: string }) => {
  const response = await tempoApi()
    .post('api/v1/work-integration/jira/oauth-client/authorization-response', {
      json: { returnUrl },
    })
    .json<JiraOauthResponseResponse>();

  return response;
};

const getJiraItemLevelMappings = async ({ instanceId }: { instanceId: string }) => {
  const response = await tempoApi()
    .get(
      `api/v1/work-integration/integrations/jira/mappings/item-level-mappings?instanceId=${instanceId}`,
    )
    .json<JiraItemLevelMapping[]>();
  return response;
};

const getJiraItemTypeMappings = async ({ instanceId }: { instanceId: string }) => {
  const response = await tempoApi()
    .get(
      `api/v1/work-integration/integrations/jira/mappings/item-type-mappings?instanceId=${instanceId}`,
    )
    .json<JiraItemTypeMapping[]>();
  return response;
};

export const JiraIntegrationAPI = {
  getInstances,
  getJiraInstanceIssueTypes,
  getJiraFields,
  getJiraIssueTypesAndStatuses,
  getJiraIssueTypesWithHierarchy,
  getJiraProjects,
  getJiraWorkItemCount,
  validateJiraJql,
  jiraOAuthRequest,
  jiraOAuthResponse,
  getJiraItemLevelMappings,
  getJiraItemTypeMappings,
} as const;
